/**
 * The filter bar, written into the address bar — so a reload keeps what you
 * were looking at, and a link carries it to another device.
 *
 * Values are names, not ids: `?is=spending&tag=food,none&plan=rent` reads as
 * what it does. Names are matched case-insensitively when they come back, and
 * anything that no longer exists is dropped rather than breaking the load.
 */
export const NOTHING = 'none'

/** The list filters, by the key they take in the query string. */
export const URL_KEYS = { kinds: 'is', status: 'status', files: 'files', tags: 'tag', categories: 'category' }

/** The words the status filter goes by, in the address bar as on screen. */
export const STATUS_WORDS = ['pending', 'settled']

/** The words the files filter goes by: with files attached, or none. */
export const FILES_WORDS = ['attached', 'unattached']

/** The two bounds, which hold one date each rather than a list. */
export const DATE_KEYS = { from: 'from', to: 'to' }

/** The two amount bounds, as plain numbers. */
export const AMOUNT_KEYS = { amountFrom: 'min', amountTo: 'max' }

const KIND_TO_WORD = { expense: 'spending', income: 'received', savings: 'saving' }
const WORD_TO_KIND = { spending: 'expense', received: 'income', saving: 'savings' }

export const kindWord = (kind) => KIND_TO_WORD[kind] ?? kind
export const wordKind = (word) => WORD_TO_KIND[String(word).toLowerCase()] ?? null

/**
 * @param {object} view  { search, kinds, tags, categories } — the lists
 *                       already turned into names
 * @returns {string} a query string, `''` when there is nothing to say
 */
export function encodeView(view = {}) {
  const parts = []
  // Encoded by hand rather than with URLSearchParams, which would turn the
  // separating commas into %2C and make the address bar unreadable.
  const add = (key, value) => parts.push(`${key}=${encodeURIComponent(value).replace(/%2C/g, ',')}`)

  for (const [key, param] of Object.entries(URL_KEYS)) {
    const values = (view[key] ?? []).filter(Boolean)
    if (values.length) add(param, values.join(','))
  }
  for (const [key, param] of Object.entries(DATE_KEYS)) {
    if (view[key]) add(param, view[key])
  }
  for (const [key, param] of Object.entries(AMOUNT_KEYS)) {
    if (view[key] != null) add(param, String(view[key]))
  }
  const search = String(view.search ?? '').trim()
  if (search) add('q', search)
  // The default sorting says nothing at all.
  if (view.sort && `${view.sort.by}-${view.sort.dir}` !== 'date-desc') {
    add('sort', `${view.sort.by}-${view.sort.dir}`)
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

/**
 * @param {string} query  `location.search`
 * @returns {object} { search, kinds, tags, categories } of plain names
 */
export function decodeView(query = '') {
  const params = new URLSearchParams(String(query).replace(/^\?/, ''))
  const size = (value) => {
    const number = Number(value)
    return value !== null && value !== '' && Number.isFinite(number) ? Math.abs(number) : null
  }
  const list = (param) =>
    (params.get(param) ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
  const [by, dir] = (params.get('sort') ?? '').split('-')
  return {
    search: params.get('q') ?? '',
    sort: {
      by: ['date', 'description', 'amount'].includes(by) ? by : 'date',
      dir: dir === 'asc' ? 'asc' : 'desc',
    },
    kinds: list(URL_KEYS.kinds),
    status: list(URL_KEYS.status).map((word) => word.toLowerCase()).filter((word) => STATUS_WORDS.includes(word)),
    files: list(URL_KEYS.files).map((word) => word.toLowerCase()).filter((word) => FILES_WORDS.includes(word)),
    tags: list(URL_KEYS.tags),
    categories: list(URL_KEYS.categories),
    // Only a real calendar day counts; anything else is left out.
    from: /^\d{4}-\d{2}-\d{2}$/.test(params.get('from') ?? '') ? params.get('from') : null,
    to: /^\d{4}-\d{2}-\d{2}$/.test(params.get('to') ?? '') ? params.get('to') : null,
    amountFrom: size(params.get('min')),
    amountTo: size(params.get('max')),
  }
}
